import { getClassNames } from '@websolutespa/bom-core';
import React, { forwardRef, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { getVariantKey, useVariants } from '../../variants';
import { UIStyledComponentProps } from '../types';
import { getCssResponsive } from '../utils';
import { AccordionProps } from './accordion';
import { AccordionGroupContext } from './accordion-group.context';
import { accordionVariants, IAccordionVariants } from './accordion.variants';

const AccordionGroupStyle = styled.div<AccordionGroupProps>`
  ${props => getVariantKey('group', props.variant, props.variants)}
  ${props => getCssResponsive(props)}
`;

export type AccordionGroupProps = UIStyledComponentProps<{
  className?: string;
  accordion?: boolean;
  variant?: keyof typeof accordionVariants | (string & {});
  variants?: IAccordionVariants;
  children?: ReactNode;
}>;

export const AccordionGroup = forwardRef<HTMLDivElement, React.PropsWithChildren<AccordionGroupProps>>(
  function AccordionGroup({
    accordion = true,
    className,
    variant,
    variants,
    children,
    ...props
  }, ref) {
    const globalVariants = useVariants('accordion', accordionVariants);
    variant = variant || 'default';
    variants = variants || globalVariants;
    const [index, setIndex] = useState(-1);
    const setOpen = (open: boolean, i: number) => {
      if (!accordion) {
        return;
      }
      if (open && index !== i) {
        setIndex(i);
      }
      if (!open && index === i) {
        setIndex(-1);
      }
    };
    const mappedChildren = React.Children.map(children, (child, i) => {
      if (!React.isValidElement<AccordionProps>(child)) {
        return null;
      }
      return React.cloneElement(child, {
        ...child.props,
        open: accordion ? i === index : undefined,
        setOpen: accordion ? (open: boolean) => setOpen(open, i) : undefined,
        index: i,
      });
    });
    const classNames = getClassNames('accordion-group', className);
    return (
      <AccordionGroupContext.Provider value={{
        accordion,
        index: 0,
        variant,
        variants,
      }}>
        <AccordionGroupStyle
          className={classNames}
          variant={variant}
          variants={variants}
          ref={ref}
          {...props}
        >
          {mappedChildren}
        </AccordionGroupStyle>
      </AccordionGroupContext.Provider>
    );
  });

AccordionGroup.displayName = 'AccordionGroup';
